import { Component, TemplateRef, ViewChild, OnInit } from "@angular/core";
import { HttpEventType } from "@angular/common/http";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  DocumentosInvestigacionService,
  DocumentosInvestigacion,
  DocumentosInvestigacionPayload,
} from "../../../../../shared/services/documentos-investigacion.service";
import { DigitalesService } from "../../../../../shared/services/digitales.service";
import { AdministracionService } from "../../../../../shared/services/administracion.services";
import { EstadosDocumentoService } from "../../../../../shared/services/estados-documento.service";
import { gpcGoogleService } from "../../../../../shared/services/gpcGoogle.service";
import { GoogleStoragePreviewService } from "../../../../../shared/services/google-storage-preview.service";

function normalize(str: any) {
  return str
    ? str
        .toString()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ")
        .trim()
    : "";
}

@Component({
  selector: "app-documentos-investigacion",
  templateUrl: "./documentos-investigacion.component.html",
  styleUrls: ["./documentos-investigacion.component.scss"],
})
export class DocumentosInvestigacionComponent implements OnInit {
  // Form único para crear/editar
  formDocumento!: FormGroup;

  // Modal único
  @ViewChild("modalDocumento") modalDocumento!: TemplateRef<any>;
  modalDocumentoRef!: NgbModalRef;

  // Modo del modal: true = editando un documento existente, false = creando uno nuevo
  modoEdicion = false;

  // Datos
  historial: DocumentosInvestigacion[] = [];
  cargandoHistorial = false;
  estadosDocumento: any[] = [];
  empresaNit: string | null = null;

  // Tabla
  page1 = 1;
  pageSize1 = 10;
  searchText = "";

  // Edición
  editandoId: number | string | null = null;
  codigoOriginal: string | null = null;
  cargandoGuardado = false;

  // Archivo (crear o reemplazar en edición)
  archivoSeleccionado: File | null = null;

  hoy = "";

  // TODO: confirmar el id real del catálogo "digitales" para este documento
  private readonly idDigital = 9; // id de "digital" para documentos de investigación
  private readonly prefijoDigital = "DOCINVESTIGACION";
  private readonly nombreArchivoPrefijo = "DOCINVESTIGACION_documental";
  private readonly nombreArchivoSufijo = "PESV";

  // Construcción de rutas y nombres de archivos para los documentos de investigación
  private construirNombreArchivo(): string {
    return `${this.nombreArchivoPrefijo}.pdf`;
  }

  private construirCarpetaDocumento(codigo: string): string {
    return `${this.empresaNit}/pesv/documental/documentos-investigacion`;
  }

  private construirRutaCompleta(item: any): string {
    console.log("Construyendo ruta completa para el documento:", item);
    console.log("Construyendo ruta completa para el documento:", item.codigo);
    console.log("Construyendo ruta completa para el documento:", item.id);
    return `${this.construirCarpetaDocumento(item.codigo)}/${item.id}/${this.construirNombreArchivo()}`;
  }

  get tieneVigente(): boolean {
    return (this.historial || []).some((item) => this.esVigente(item));
  }

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private documentosInvestigacionService: DocumentosInvestigacionService,
    private digitalesService: DigitalesService,
    private administracionesService: AdministracionService,
    private estadoDocumentosService: EstadosDocumentoService,
    private gpcGoogle: gpcGoogleService,
    private googleStoragePreview: GoogleStoragePreviewService,
  ) {}

  ngOnInit(): void {
    this.formDocumento = this.fb.group({
      codigo: ["", Validators.required],
      version: ["", Validators.required],
      fecha_designacion: ["", Validators.required],
      fecha_vencimiento: ["", Validators.required],
      motivo_creacion: ["", Validators.required],
      estado: [null, Validators.required],
    });

    this.hoy = new Date().toISOString().split("T")[0];

    this.cargarNitEmpresa();
    this.cargarEstadosDocumento();
    this.cargarHistorial();

    // Autocalcular vencimiento a 3 años desde la fecha de designación
    // Solo aplica cuando se está creando; en edición no se debe pisar el valor guardado.
    this.formDocumento.get("fecha_designacion")?.valueChanges.subscribe((fecha) => {
      if (!fecha || this.modoEdicion) return;
      const vencimiento = new Date(fecha);
      vencimiento.setFullYear(vencimiento.getFullYear() + 3);
      this.formDocumento.patchValue({
        fecha_vencimiento: vencimiento.toISOString().split("T")[0],
      });
    });
  }

  // ===========================
  // Carga inicial
  // ===========================
  private cargarNitEmpresa(): void {
    this.administracionesService.obtenerTodas().subscribe({
      next: (res: any) => {
        const lista = Array.isArray(res)
          ? res
          : Array.isArray(res?.administraciones)
            ? res.administraciones
            : [];
        if (lista.length > 0) {
          this.empresaNit = lista[0].nit?.toString() || null;
        }
      },
      error: (err: any) =>
        console.error("❌ Error al cargar NIT de la empresa:", err),
    });
  }

  private cargarEstadosDocumento(): void {
    this.estadoDocumentosService.getAllEstados().subscribe({
      next: (resp: any) => {
        this.estadosDocumento = Array.isArray(resp) ? resp : [];
        const estadoBorrador = this.estadosDocumento.find(
          (estado) => normalize(estado?.nombre) === "borrador",
        );
        if (estadoBorrador && !this.modoEdicion) {
          this.formDocumento.patchValue({ estado: estadoBorrador.id });
        }
      },
      error: (error: any) => {
        console.error(error);
        Swal.fire(
          "Error",
          "No fue posible cargar los estados del documento.",
          "error",
        );
      },
    });
  }

  cargarHistorial(): void {
    this.cargandoHistorial = true;
    this.documentosInvestigacionService.getAll().subscribe({
      next: (lista: any[]) => {
        this.historial = Array.isArray(lista) ? lista : [];
        this.cargandoHistorial = false;
      },
      error: (err) => {
        console.error("Error obteniendo documentos de investigación:", err);
        this.historial = [];
        this.cargandoHistorial = false;
      },
    });
  }

  // ===========================
  // Utilidades de estado / fecha
  // ===========================
  private obtenerNombreEstado(item: any): string {
    const nombreRespuesta = item?.DocumentosEstadosData?.nombre;
    if (nombreRespuesta) return normalize(nombreRespuesta);

    const estadoCatalogo = this.estadosDocumento.find(
      (estado) => Number(estado?.id) === Number(item?.estado),
    );
    return normalize(estadoCatalogo?.nombre);
  }

  esActivo(item: any): boolean {
    return this.obtenerNombreEstado(item) === "vigente";
  }

  esVigente(item: any): boolean {
    if (!this.esActivo(item)) return false;

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const inicio = item?.fecha_designacion
      ? new Date(item.fecha_designacion)
      : null;
    const fin = item?.fecha_vencimiento
      ? new Date(item.fecha_vencimiento)
      : null;

    if (inicio) {
      inicio.setHours(0, 0, 0, 0);
      if (inicio > hoy) return false;
    }
    if (fin) {
      fin.setHours(0, 0, 0, 0);
      if (fin < hoy) return false;
    }
    return true;
  }

  private obtenerEstadoTexto(item: any): string {
    return this.obtenerNombreEstado(item).toUpperCase();
  }

  puedeEditar(item: any): boolean {
    return this.obtenerEstadoTexto(item) === "BORRADOR";
  }

  private mensajeNoEditable(item: any): string {
    switch (this.obtenerEstadoTexto(item)) {
      case "VIGENTE":
        return "El documento está vigente. Para modificarlo debe crear una nueva versión.";
      case "OBSOLETA":
        return "El documento está obsoleto. Solo se permite consulta.";
      default:
        return "Solo los documentos en estado BORRADOR pueden editarse.";
    }
  }

  formatDateTodo(fecha: any): string {
    if (!fecha) return "N/A";
    const d = new Date(fecha);
    return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${d.getUTCFullYear()}`;
  }

  private normalizarFechaParaInput(fecha: any): string {
    if (!fecha) return "";
    const valor = String(fecha);
    if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) return valor;
    const d = new Date(fecha);
    if (Number.isNaN(d.getTime())) return "";
    return this.formatearFechaParaInput(d);
  }

  private formatearFechaParaInput(fecha: Date): string {
    const año = fecha.getFullYear();
    const mes = String(fecha.getMonth() + 1).padStart(2, "0");
    const dia = String(fecha.getDate()).padStart(2, "0");
    return `${año}-${mes}-${dia}`;
  }

  private normalizarCodigo(codigo: string): string {
    return String(codigo || "")
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  }

  /**
   * Sugiere la siguiente versión a partir del historial existente (mayor + 1 en el minor).
   */
  private calcularSiguienteVersion(): string {
    const versiones = (this.historial || [])
      .map((item: any) => String(item?.version || "").trim())
      .filter((v) => /^\d+(\.\d+)?$/.test(v))
      .map((v) => parseFloat(v));

    if (!versiones.length) return "1.0";

    const mayor = Math.max(...versiones);
    return (mayor + 0.1).toFixed(1);
  }

  private obtenerMensajeError(err: any, porDefecto: string): string {
    const mensaje =
      err?.error?.message || err?.error?.error || err?.message || porDefecto;
    if (typeof mensaje !== "string") return porDefecto;
    if (mensaje.toLowerCase().startsWith("http failure response for"))
      return porDefecto;
    return mensaje;
  }

  private mostrarSwal(
    icon: "success" | "error" | "warning" | "info" | "question",
    titulo: string,
    texto?: string,
  ): void {
    Swal.fire({
      icon,
      title: titulo,
      text: texto,
      confirmButtonColor: "#0d6efd",
    });
  }

  // ===========================
  // Listado / filtro / paginación
  // ===========================
  get historialVista(): any[] {
    const base = this.historial || [];
    const search = normalize(this.searchText);
    if (!search) return base;

    return base.filter((item: any) => {
      const codigo = normalize(item?.codigo || "");
      const version = normalize(item?.version || "");
      const fechaDesignacion = normalize(
        this.formatDateTodo(item?.fecha_designacion),
      );
      const fechaVencimiento = normalize(
        this.formatDateTodo(item?.fecha_vencimiento),
      );
      const estado = this.obtenerNombreEstado(item);
      return (
        codigo.includes(search) ||
        version.includes(search) ||
        fechaDesignacion.includes(search) ||
        fechaVencimiento.includes(search) ||
        estado.includes(search)
      );
    });
  }

  aplicarFiltro(): void {
    this.page1 = 1;
  }

  // ===========================
  // Exportar
  // ===========================
  exportarExcel(): void {
    const datos = this.historialVista || [];
    if (!datos.length) {
      this.mostrarSwal(
        "info",
        "Sin datos",
        "No hay documentos de investigación para exportar",
      );
      return;
    }

    const filas = datos.map((item: any) => ({
      CODIGO: item?.codigo || "",
      VERSION: item?.version || "",
      DESIGNACION: this.formatDateTodo(item?.fecha_designacion),
      VENCIMIENTO: this.formatDateTodo(item?.fecha_vencimiento),
      ESTADO: item.DocumentosEstadosData?.nombre || "",
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(filas);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Documentos Investigacion");
    XLSX.writeFile(wb, `documentos_investigacion_${Date.now()}.xlsx`);
    this.mostrarSwal("success", "Éxito", "Exportación a Excel completada");
  }

  exportarPdf(): void {
    const datos = this.historialVista || [];
    if (!datos.length) {
      this.mostrarSwal(
        "info",
        "Sin datos",
        "No hay documentos de investigación para exportar",
      );
      return;
    }

    const doc = new jsPDF("p", "pt");
    doc.setFontSize(12);
    doc.text("Historial de Documentos de Investigación", 40, 35);

    autoTable(doc, {
      head: [["CODIGO", "VERSION", "DESIGNACION", "VENCIMIENTO", "ESTADO"]],
      body: datos.map((item: any) => [
        item?.codigo || "",
        item?.version || "",
        this.formatDateTodo(item?.fecha_designacion),
        this.formatDateTodo(item?.fecha_vencimiento),
        item.DocumentosEstadosData?.nombre || "",
      ]),
      startY: 50,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [13, 110, 253] },
    });

    doc.save(`documentos_investigacion_${Date.now()}.pdf`);
    this.mostrarSwal("success", "Éxito", "Exportación a PDF completada");
  }

  // ===========================
  // Modal único (crear + editar)
  // ===========================
  private abrirModal(): void {
    this.modalDocumentoRef = this.modalService.open(this.modalDocumento, {
      centered: true,
      size: "lg",
      backdrop: "static",
    });
    this.modalDocumentoRef.result.finally(() => {
      this.resetearFormulario();
    });
  }

  private resetearFormulario(): void {
    const estadoBorrador = this.estadosDocumento.find(
      (estado) => normalize(estado?.nombre) === "borrador",
    );
    this.formDocumento.reset({
      version: "",
      estado: estadoBorrador ? estadoBorrador.id : null,
    });
    this.archivoSeleccionado = null;
    this.modoEdicion = false;
    this.editandoId = null;
    this.codigoOriginal = null;
    this.cargandoGuardado = false;
  }

  abrirModalCrear(): void {
    if (this.tieneVigente) {
      Swal.fire({
        icon: "warning",
        title: "No se puede crear",
        text: "Ya existe un documento de investigación vigente. Inactive el actual o espere a que venza para crear uno nuevo.",
      });
      return;
    }

    this.modoEdicion = false;
    this.editandoId = null;
    this.codigoOriginal = null;
    this.archivoSeleccionado = null;

    const estadoBorrador = this.estadosDocumento.find(
      (estado) => normalize(estado?.nombre) === "borrador",
    );
    this.formDocumento.reset({
      codigo: "",
      version: this.calcularSiguienteVersion(),
      fecha_designacion: "",
      fecha_vencimiento: "",
      estado: estadoBorrador ? estadoBorrador.id : null,
    });

    this.abrirModal();
  }

  editarDesdeHistorial(item: any): void {
    if (!item || item.id == null) {
      this.mostrarSwal(
        "error",
        "Error",
        "No se encontró el identificador del documento a editar",
      );
      return;
    }

    if (!this.puedeEditar(item)) {
      Swal.fire({
        icon: "warning",
        title: "No se puede editar",
        text: this.mensajeNoEditable(item),
      });
      return;
    }

    this.modoEdicion = true;
    this.editandoId = item.id;
    this.codigoOriginal = this.normalizarCodigo(item.codigo || "");
    this.archivoSeleccionado = null;

    this.formDocumento.patchValue({
      codigo: item.codigo || "",
      version: item.version || "",
      fecha_designacion: this.normalizarFechaParaInput(item.fecha_designacion),
      fecha_vencimiento: this.normalizarFechaParaInput(item.fecha_vencimiento),
      estado: item.estado ?? null,
    });

    this.abrirModal();
  }

  // ===========================
  // Archivo (crear o reemplazar en edición)
  // ===========================
  onArchivoSeleccionado(event: any): void {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.asignarArchivo(file);
    event.target.value = "";
  }

  onDragOverArchivo(event: DragEvent): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).classList.add("drag-over");
  }

  onDragLeaveArchivo(event: DragEvent): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).classList.remove("drag-over");
  }

  onDropArchivo(event: DragEvent): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).classList.remove("drag-over");
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.asignarArchivo(file);
    event.dataTransfer?.clearData();
  }

  private asignarArchivo(file: File): void {
    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    if (extension !== "pdf") {
      Swal.fire("Error", "Solo se permiten archivos PDF", "error");
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      Swal.fire("Error", "El archivo no debe superar los 10MB", "error");
      return;
    }

    this.archivoSeleccionado = file;
  }

  removerArchivoSeleccionado(): void {
    this.archivoSeleccionado = null;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  // ===========================
  // Guardar (crear o editar según modoEdicion)
  // ===========================
  guardar(): void {
    if (this.formDocumento.invalid) {
      this.formDocumento.markAllAsTouched();
      Swal.fire({
        icon: "warning",
        title: "Campos requeridos",
        text: "Debe completar toda la información.",
      });
      return;
    }

    if (this.modoEdicion) {
      this.guardarEdicion();
    } else {
      this.guardarCreacion();
    }
  }

  private guardarCreacion(): void {
    if (this.tieneVigente) {
      Swal.fire({
        icon: "warning",
        title: "No se puede guardar",
        text: "Ya existe un documento de investigación vigente. Inactive el actual o espere a que venza para crear uno nuevo.",
      });
      return;
    }

    if (!this.archivoSeleccionado) {
      Swal.fire({
        icon: "warning",
        title: "Documento requerido",
        text: "Debe adjuntar el documento PDF.",
      });
      return;
    }

    const valores = this.formDocumento.value;
    console.log("Valores del formulario:", valores);  
    const codigoNuevo = this.normalizarCodigo(valores.codigo);

    const codigoDuplicado = (this.historial || []).some(
      (item: any) => this.normalizarCodigo(item?.codigo || "") === codigoNuevo,
    );

    if (codigoDuplicado) {
      this.mostrarSwal(
        "warning",
        "Código duplicado",
        `El código ${valores.codigo} ya existe. Indique un código diferente.`,
      );
      return;
    }

    const payload: DocumentosInvestigacionPayload = {
      codigo: valores.codigo,
      version: valores.version,
      fecha_designacion: valores.fecha_designacion,
      fecha_vencimiento: valores.fecha_vencimiento,
      motivo_creacion: valores.motivo_creacion,
      estado: valores.estado,
    };
    console.log("Payload a enviar:", payload);
    const archivo = this.archivoSeleccionado;

    Swal.fire({
      title: "Guardando documento de investigación...",
      text: "Espere un momento",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    this.documentosInvestigacionService.create(payload).subscribe({
      next: (resp: any) => {
        Swal.close();
        const idCreado = resp?.id ?? resp?.data?.id ?? null;
        const exitoExplicito = resp?.success === true || resp?.ok === true;
        const ok = !!idCreado || exitoExplicito;

        if (!ok) {
          Swal.fire({
            icon: "warning",
            title: "Atención",
            text: resp?.message || "Respuesta no exitosa",
          });
          return;
        }

        if (archivo) {
          this.subirArchivo(
            archivo,
            idCreado,
            this.construirCarpetaDocumento(codigoNuevo),
            this.construirNombreArchivo(),
            this.prefijoDigital,
            "Documento de investigación y archivo guardados correctamente",
          );
        } else {
          this.mostrarSwal(
            "success",
            "Éxito",
            resp?.message || "Documento de investigación guardado correctamente",
          );
        }

        this.modalDocumentoRef?.close();
        this.cargarHistorial();
      },
      error: (error) => {
        const mensaje = this.obtenerMensajeError(error, "Error inesperado");
        Swal.fire({
          icon: "error",
          title: "No se pudo guardar",
          text: mensaje,
        });
      },
    });
  }

  private guardarEdicion(): void {
    if (!this.editandoId) {
      this.mostrarSwal(
        "error",
        "Error",
        "No se encontró el identificador del documento",
      );
      return;
    }

    const valores = this.formDocumento.value;
   // console.log("Valores del formulario:", valores);
    const codigo = this.normalizarCodigo(valores.codigo);
    const version = String(valores.version || "").trim();
    const fechaDesignacion = String(valores.fecha_designacion || "").trim();
    const fechaVencimiento = String(valores.fecha_vencimiento || "").trim();
    const codigoOriginal = this.codigoOriginal || "";
    const sinCambioDeCodigo = !!codigoOriginal && codigo === codigoOriginal;

    const hoy = this.formatearFechaParaInput(new Date());
    if (fechaDesignacion > hoy) {
      this.mostrarSwal(
        "warning",
        "Fecha inválida",
        "La fecha de designación no puede ser futura en edición",
      );
      return;
    }

    if (fechaVencimiento < fechaDesignacion) {
      this.mostrarSwal(
        "warning",
        "Fecha inválida",
        "La fecha de vencimiento no puede ser anterior a la fecha de designación",
      );
      return;
    }

    this.documentosInvestigacionService.getAll().subscribe({
      next: (lista: any[]) => {
        const existente = (Array.isArray(lista) ? lista : []).find(
          (item: any) => this.normalizarCodigo(item?.codigo || "") === codigo,
        );
        const duplicado =
          !!existente && String(existente?.id) !== String(this.editandoId);

        if (duplicado) {
          this.mostrarSwal(
            "warning",
            "Código duplicado",
            `El código ${codigo} ya existe. Cambia el código para poder guardar.`,
          );
          return;
        }

        this.cargandoGuardado = true;

        const payload = sinCambioDeCodigo
          ? {
              version,
              fecha_designacion: fechaDesignacion,
              fecha_vencimiento: fechaVencimiento,
              motivo_creacion: valores.motivo_creacion,
              estado: valores.estado,
            }
          : {
              codigo,
              version,
              fecha_designacion: fechaDesignacion,
              fecha_vencimiento: fechaVencimiento,
              motivo_creacion: valores.motivo_creacion,
              estado: valores.estado,
            };

        const archivo = this.archivoSeleccionado;

        this.documentosInvestigacionService
          .update(this.editandoId as number, payload)
          .subscribe({
            next: () => {
              if (!archivo) {
                this.finalizarGuardado(
                  "Documento de investigación actualizado correctamente",
                );
                return;
              }

              const nombreArchivo = this.construirNombreArchivo();
              const carpeta = this.construirCarpetaDocumento(codigo);

              this.digitalesService
                .subirArchivo(
                  archivo,
                  this.idDigital,
                  carpeta,
                  nombreArchivo,
                  this.prefijoDigital,
                )
                .subscribe({
                  next: (event: any) => {
                    if (event.type === HttpEventType.Response) {
                      this.finalizarGuardado(
                        "Documento y archivo actualizados correctamente",
                      );
                    }
                  },
                  error: () => {
                    this.cargandoGuardado = false;
                    this.mostrarSwal(
                      "error",
                      "Error",
                      "Se actualizó el registro, pero no se pudo reemplazar el documento.",
                    );
                    this.cargarHistorial();
                  },
                });
            },
            error: (err) => {
              this.cargandoGuardado = false;
              this.mostrarSwal(
                "error",
                "Error",
                this.obtenerMensajeError(
                  err,
                  "No se pudo actualizar el documento de investigación",
                ),
              );
            },
          });
      },
      error: () =>
        this.mostrarSwal(
          "error",
          "Error",
          "No se pudo verificar el documento de investigación",
        ),
    });
  }

  private subirArchivo(
    archivo: File,
    idDigital: number,
    carpeta: string,
    nombreArchivo: string,
    prefijo: string,
    mensajeExito: string,
  ): void {
    this.digitalesService
      .subirArchivo(archivo, idDigital, carpeta, nombreArchivo, prefijo)
      .subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.Response) {
            this.mostrarSwal("success", "Éxito", mensajeExito);
            this.cargarHistorial();
          }
        },
        error: (err: any) => {
          console.error("❌ Error al subir:", err);
          this.mostrarSwal(
            "error",
            "Error",
            this.obtenerMensajeError(err, "Error al subir el documento"),
          );
        },
      });
  }

  private finalizarGuardado(mensaje: string): void {
    this.cargandoGuardado = false;
    this.mostrarSwal("success", "Éxito", mensaje);
    this.modalDocumentoRef?.close();
    this.cargarHistorial();
  }

  // ===========================
  // Cambiar estado
  // ===========================
  cambiarEstado(item: any, activar: boolean): void {
    const id = item?.id;
    if (!id) {
      this.mostrarSwal(
        "error",
        "Error",
        "No se encontró el identificador del documento",
      );
      return;
    }

    const yaEnEstado = activar ? this.esActivo(item) : !this.esActivo(item);
    if (yaEnEstado) {
      this.mostrarSwal(
        "info",
        "Sin cambios",
        activar
          ? "El documento ya está activo"
          : "El documento ya está inactivo",
      );
      return;
    }

    Swal.fire({
      title: "Confirmar acción",
      text: activar
        ? "¿Deseas activar este documento?"
        : "¿Deseas inactivar este documento?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí",
      cancelButtonText: "No",
      confirmButtonColor: "#0d6efd",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const request$ = activar
        ? this.documentosInvestigacionService.activar(id)
        : this.documentosInvestigacionService.inactivar(id);
      request$.subscribe({
        next: () => {
          this.mostrarSwal(
            "success",
            "Éxito",
            activar
              ? "Documento activado correctamente"
              : "Documento inactivado correctamente",
          );
          this.cargarHistorial();
        },
        error: (err) => {
          this.mostrarSwal(
            "error",
            "Error",
            this.obtenerMensajeError(
              err,
              activar
                ? "No se pudo activar el documento"
                : "No se pudo inactivar el documento",
            ),
          );
        },
      });
    });
  }

  // ===========================
  // Visualizar / descargar
  // ===========================
  visualizarPDF(item: any): void {
    if (!item.codigo) {
      Swal.fire("Error", "No se puede visualizar sin código", "error");
      return;
    }

    Swal.fire({
      title: "Confirmar visualización",
      text: "¿Deseas visualizar el documento: Documento de Investigación?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Visualizar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      if (!this.empresaNit) {
        Swal.close();
        Swal.fire(
          "Error",
          "No se pudo obtener el NIT de la empresa para visualizar el documento.",
          "error",
        );
        return;
      }

      const ruta = this.construirRutaCompleta(item);

      this.gpcGoogle.getSignedUrlFiles(ruta).subscribe({
        next: (resp) => {
          Swal.close();
          if (!resp?.url) {
            Swal.fire(
              "Error",
              "No se pudo obtener la URL del documento.",
              "error",
            );
            return;
          }
          this.googleStoragePreview.open(
            resp.url,
            "Documento de Investigación PESV",
          );
        },
        error: (err) => {
          Swal.close();
          console.error("❌ Error al obtener la URL del documento:", err);
          Swal.fire(
            "Error",
            "No se pudo obtener la URL del documento.",
            "error",
          );
        },
      });
    });
  }

  descargarDocumento(item: any): void {
    if (!item.codigo) {
      Swal.fire("Error", "No se puede descargar sin código", "error");
      return;
    }

    if (!this.empresaNit) {
      Swal.fire(
        "Error",
        "No se pudo obtener el NIT de la empresa para descargar el documento.",
        "error",
      );
      return;
    }

    Swal.fire({
      title: "Descargando...",
      text: "Por favor espera mientras se descarga el documento.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    const ruta = this.construirRutaCompleta(item);

    this.gpcGoogle.getSignedUrlFiles(ruta).subscribe({
      next: (resp) => {
        Swal.close();
        if (!resp?.url) {
          Swal.fire(
            "Error",
            "No se pudo obtener la URL firmada del documento.",
            "error",
          );
          return;
        }
        this.descargarDesdeUrlFirmada(
          resp.url,
          `DOCUMENTO_INVESTIGACION_${item.id}.pdf`,
        );
      },
      error: (err) => {
        Swal.close();
        console.error("❌ Error al obtener la URL firmada del documento:", err);
        Swal.fire(
          "Error",
          "No se pudo obtener la URL firmada del documento.",
          "error",
        );
      },
    });
  }

  private descargarDesdeUrlFirmada(url: string, fileName: string): void {
    fetch(url)
      .then((response) => {
        if (!response.ok)
          throw new Error(`Error al descargar archivo: ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        const objectUrl = window.URL.createObjectURL(blob);
        const enlace = document.createElement("a");
        enlace.href = objectUrl;
        enlace.download = fileName;
        enlace.style.display = "none";
        document.body.appendChild(enlace);
        enlace.click();
        document.body.removeChild(enlace);
        window.URL.revokeObjectURL(objectUrl);
      })
      .catch((error) => {
        console.error("Error al descargar URL firmada:", error);
        Swal.fire("Error", "No se pudo descargar el documento.", "error");
      });
  }
}